// LEGACY BRIDGE — TODO: delete this entire file when the CoffeeScript Beacon
// views are modernized to React. There's no ticket for that work yet; once it
// exists, every reference marked `LEGACY BRIDGE` should be removed together.
//
// Why this exists:
// - Beacon's React MRE map polls /beacon/:id via useBeaconPolling.
// - The legacy CoffeeScript stack (BeaconView, StatsView, AvatarView, the
//   page-state machine, the no-recent-data alert) is still alive and was,
//   until this commit, polling the same endpoint independently — doubling
//   our backend load per Beacon viewer.
// - This bridge lets React's poll feed the legacy Backbone LiveActivity
//   model so its existing `change:*` / `sync` / `failedsync` events still
//   fire and StatsView/AvatarView/BeaconView keep working unchanged.
//
// When the legacy views are ported to React, delete:
//   - this file
//   - the calls to notifyLegacyBeaconActivity / notifyLegacyBeaconActivityFailedSync
//     in useBeaconPolling.ts
//   - the `Strava.Beacon.beaconActivity = @beaconActivity` assignment in
//     BeaconView.js.coffee
//   - the entire CoffeeScript Beacon stack (see the modernization-cleanup
//     bullet on this PR for the file list)

// `any` here is intentional — the legacy Backbone model is untyped CoffeeScript
// and we only call two methods on it (extend, trigger). Active's TS toolchain
// uses babel-eslint, which doesn't understand `unknown` or `as` casts in plain
// .ts files, so a typed lookup with assertions isn't an option here.
/* eslint-disable no-undef */

// The legacy CoffeeScript assigns Strava.Beacon.beaconActivity in
// BeaconView.coffee's constructor. Other Active code accesses window.Strava
// the same way — see app/javascript/utils/I18n.js, utils/analytics.js.
function getLegacyBeaconActivity(): any {
  return window.Strava && window.Strava.Beacon && window.Strava.Beacon.beaconActivity;
}

/**
 * Forward a successful poll response into the legacy Backbone model so its
 * `change:*` and `sync` listeners (StatsView, AvatarView, BeaconView) fire.
 * No-op if the legacy stack isn't on the page (e.g., during tests or after
 * the legacy code is eventually deleted).
 */
export function notifyLegacyBeaconActivity(update: any): void {
  const activity = getLegacyBeaconActivity();
  if (activity) activity.extend(update);
}

/**
 * Forward a poll failure into the legacy model so its `failedsync` listener
 * (the connection-err / not-available alert) fires.
 *
 * Adapts the axios error shape to the jqXHR shape the legacy
 * BeaconView#handleFailedSync expects (`error.status`):
 *   - offline / timeout / CORS-blocked: axios has no `response` → status 0,
 *     which legacy maps to the CONNECTION_ERR alert.
 *   - server returned an HTTP error: axios sets `error.response.status` →
 *     pass through (legacy maps 503 to NOT_AVAILABLE; other codes show the
 *     alert with an empty message, same as legacy behavior).
 */
export function notifyLegacyBeaconActivityFailedSync(error: any): void {
  const activity = getLegacyBeaconActivity();
  if (!activity) return;
  const status = (error && error.response && error.response.status) || 0;
  activity.trigger("failedsync", { status });
}

